import React, { useState, useEffect, useCallback } from 'react';
import {
    Stack,
    Text,
    TextField,
    PrimaryButton,
    DefaultButton,
    IconButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    IStackTokens,
    mergeStyleSets,
    FontWeights,
    Separator,
    Label,
    TooltipHost,
    Dropdown,
    Dialog,
    DialogType,
    DialogFooter
} from '@fluentui/react';
import {
    QuestionCircleRegular,
    AddRegular,
    DeleteRegular,
    BrainCircuitRegular,
    SaveRegular,
    InfoRegular,
    WandRegular,
    ArrowUploadRegular,
    DocumentBulletListRegular,
    ChevronDownRegular,
    ChevronRightRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import { TagInput } from '../common/TagInput';

// Types
interface ExampleQuestion {
    question: string;
    query_pattern?: string;
    explanation?: string;
    tables_used: string[];
    columns_used: string[];
    
    // NEW: Critical fields for AI understanding
    calculation_hint?: string;      // e.g., "SUM(Sales) grouped by Region"
    sql_template?: string;          // e.g., "SELECT Region, SUM(Sales) FROM data GROUP BY Region"
    target_columns?: string[];      // Specific columns AI should use
    filter_hints?: string[];        // Common filter patterns for this question type
    expected_aggregation?: string;  // sum, average, count, etc.
    
    // Step-by-step worked example with actual numbers (non-technical, helps AI understand methodology)
    worked_example?: string;        // e.g., "Filter to Container type → 15 vessels. Avg = Sum(435M)/Count(14) = 31.08M"
}

interface ColumnInfo {
    column_name: string;
    business_name?: string;
    synonyms?: string[];
    synonyms_he?: string[];
    semantic_category?: string;
    business_role?: string;
}

interface TableInfo {
    name: string;
    columns: ColumnInfo[];
}

interface Props {
    sourceId: string;
    onSave?: () => void;
}

const stackTokens: IStackTokens = { childrenGap: 12 };

// Styles
const styles = mergeStyleSets({
    container: {
        padding: '20px'
    },
    questionCard: {
        padding: '16px',
        backgroundColor: 'white',
        border: '1px solid #e1e1e1',
        borderRadius: '8px',
        marginBottom: '12px'
    },
    questionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
    },
    questionNumber: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: '#667eea',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: FontWeights.semibold,
        fontSize: '14px',
        flexShrink: 0
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginTop: '12px'
    },
    helpText: {
        fontSize: '12px',
        color: '#666',
        marginTop: '4px'
    },
    suggestionsPanel: {
        padding: '16px',
        backgroundColor: '#f7f9fc',
        borderRadius: '8px',
        marginBottom: '20px'
    }
});

export const ExampleQuestionsEditor: React.FC<Props> = ({ sourceId, onSave }) => {
    // State
    const [questions, setQuestions] = useState<ExampleQuestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [tablesInfo, setTablesInfo] = useState<TableInfo[]>([]);
    const [detectingIndex, setDetectingIndex] = useState<number | null>(null);
    const [testingIndex, setTestingIndex] = useState<number | null>(null);
    const [testResult, setTestResult] = useState<{ index: number; result: any } | null>(null);
    
    // Bulk import state
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [bulkJsonText, setBulkJsonText] = useState('');
    const [bulkImportError, setBulkImportError] = useState<string | null>(null);
    
    // Expand/collapse state
    const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
    
    const toggleExpand = (index: number) => {
        setExpandedQuestions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };
    
    const expandAll = () => {
        setExpandedQuestions(new Set(questions.map((_, i) => i)));
    };
    
    const collapseAll = () => {
        setExpandedQuestions(new Set());
    };
    
    // Fetch existing questions and tables/columns info
    const fetchQuestions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}`);
            if (response.ok) {
                const data = await response.json();
                setQuestions(data.example_questions || []);
                
                // Extract tables and columns from semantic_mappings
                const mappings = data.semantic_mappings || {};
                const tables: TableInfo[] = [];
                
                for (const [tableName, columns] of Object.entries(mappings)) {
                    const colArray = columns as ColumnInfo[];
                    tables.push({
                        name: tableName,
                        columns: colArray.map((c: any) => ({
                            column_name: c.column_name,
                            business_name: c.business_name,
                            synonyms: c.synonyms || [],
                            synonyms_he: c.synonyms_he || [],
                            semantic_category: c.semantic_category,
                            business_role: c.business_role
                        }))
                    });
                }
                
                setTablesInfo(tables);
                console.log('[ExampleQuestions] Loaded tables info:', tables.map(t => ({ name: t.name, columnCount: t.columns.length })));
            } else {
                setError('Failed to load example questions');
            }
        } catch (err) {
            setError('Error loading example questions');
        } finally {
            setLoading(false);
        }
    }, [sourceId]);
    
    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);
    
    // Auto-detect tables and columns from question text
    const handleAutoDetect = useCallback((index: number) => {
        const question = questions[index];
        if (!question?.question?.trim()) return;
        
        setDetectingIndex(index);
        
        const questionLower = question.question.toLowerCase();
        const questionWords = questionLower.split(/[\s,?!.]+/).filter(w => w.length > 2);
        
        const matchedTables = new Set<string>();
        const matchedColumns = new Set<string>();
        
        // Helper to check if any word matches
        const matchesText = (text: string | undefined): boolean => {
            if (!text) return false;
            const textLower = text.toLowerCase();
            // Check if text appears in question or any word matches
            if (questionLower.includes(textLower)) return true;
            // Check individual words
            const textWords = textLower.split(/[\s_-]+/);
            return textWords.some(tw => 
                tw.length > 2 && questionWords.some(qw => qw === tw || qw.includes(tw) || tw.includes(qw))
            );
        };
        
        // Check each table
        for (const table of tablesInfo) {
            let tableMatched = false;
            
            // Check if table name matches
            if (matchesText(table.name)) {
                matchedTables.add(table.name);
                tableMatched = true;
            }
            
            // Check each column
            for (const col of table.columns) {
                let colMatched = false;
                
                // Check column name
                if (matchesText(col.column_name)) {
                    colMatched = true;
                }
                
                // Check business name
                if (matchesText(col.business_name)) {
                    colMatched = true;
                }
                
                // Check synonyms (English)
                if (col.synonyms?.some(syn => matchesText(syn))) {
                    colMatched = true;
                }
                
                // Check synonyms (Hebrew)
                if (col.synonyms_he?.some(syn => matchesText(syn))) {
                    colMatched = true;
                }
                
                if (colMatched) {
                    matchedColumns.add(col.column_name);
                    matchedTables.add(table.name);
                    tableMatched = true;
                }
            }
        }
        
        // Update the question with detected tables and columns
        const updated = [...questions];
        updated[index] = {
            ...updated[index],
            tables_used: Array.from(matchedTables),
            columns_used: Array.from(matchedColumns)
        };
        setQuestions(updated);
        
        setDetectingIndex(null);
        
        if (matchedTables.size > 0 || matchedColumns.size > 0) {
            setSuccess(`Auto-detected ${matchedTables.size} table(s) and ${matchedColumns.size} column(s)`);
            setTimeout(() => setSuccess(null), 3000);
        } else {
            setError('No matching tables or columns found. Try adding more specific terms to the question.');
            setTimeout(() => setError(null), 3000);
        }
    }, [questions, tablesInfo]);
    
    // Add new question
    const handleAddQuestion = () => {
        setQuestions([
            ...questions,
            {
                question: '',
                query_pattern: '',
                explanation: '',
                tables_used: [],
                columns_used: []
            }
        ]);
    };
    
    // Bulk import from JSON
    const handleBulkImport = () => {
        setBulkImportError(null);
        
        if (!bulkJsonText.trim()) {
            setBulkImportError('Please paste JSON content');
            return;
        }
        
        try {
            const parsed = JSON.parse(bulkJsonText);
            
            // Validate it's an array
            if (!Array.isArray(parsed)) {
                setBulkImportError('JSON must be an array of questions');
                return;
            }
            
            // Validate and normalize each question
            const importedQuestions: ExampleQuestion[] = [];
            const errors: string[] = [];
            
            parsed.forEach((item, index) => {
                if (!item.question || typeof item.question !== 'string') {
                    errors.push(`Item ${index + 1}: missing or invalid 'question' field`);
                    return;
                }
                
                // Normalize the question object
                const normalized: ExampleQuestion = {
                    question: item.question,
                    query_pattern: item.query_pattern || '',
                    explanation: item.explanation || '',
                    tables_used: Array.isArray(item.tables_used) ? item.tables_used : 
                                 Array.isArray(item.target_tables) ? item.target_tables : [],
                    columns_used: Array.isArray(item.columns_used) ? item.columns_used : 
                                  Array.isArray(item.target_columns) ? item.target_columns : [],
                    calculation_hint: item.calculation_hint || '',
                    sql_template: item.sql_template || '',
                    target_columns: Array.isArray(item.target_columns) ? item.target_columns : [],
                    filter_hints: Array.isArray(item.filter_hints) ? item.filter_hints : [],
                    expected_aggregation: item.expected_aggregation || '',
                    worked_example: item.worked_example || ''
                };
                
                importedQuestions.push(normalized);
            });
            
            if (errors.length > 0) {
                setBulkImportError(`Validation errors:\n${errors.join('\n')}`);
                return;
            }
            
            if (importedQuestions.length === 0) {
                setBulkImportError('No valid questions found in JSON');
                return;
            }
            
            // Add to existing questions
            setQuestions([...questions, ...importedQuestions]);
            setShowBulkImport(false);
            setBulkJsonText('');
            setSuccess(`Successfully imported ${importedQuestions.length} question(s)`);
            setTimeout(() => setSuccess(null), 5000);
            
        } catch (e: any) {
            setBulkImportError(`Invalid JSON: ${e.message}`);
        }
    };
    
    // Remove question
    const handleRemoveQuestion = (index: number) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };
    
    // Update question field
    const updateQuestion = (index: number, field: keyof ExampleQuestion, value: any) => {
        const updated = [...questions];
        updated[index] = {
            ...updated[index],
            [field]: value
        };
        setQuestions(updated);
    };
    
    // Test calculation against real data
    const handleTestCalculation = async (index: number) => {
        const question = questions[index];
        if (!question?.question?.trim()) {
            setError('Enter a question first');
            return;
        }
        
        setTestingIndex(index);
        setTestResult(null);
        setError(null);
        
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}/test-calculation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question: question.question })
            });
            
            const result = await response.json();
            
            if (response.ok) {
                setTestResult({ index, result });
                setSuccess(`Calculation test completed: ${result.aggregation}(${result.metric_column}) = ${result.result}`);
                setTimeout(() => setSuccess(null), 5000);
            } else {
                setError(result.error || 'Test calculation failed');
            }
        } catch (err) {
            setError('Error testing calculation');
        } finally {
            setTestingIndex(null);
        }
    };
    
    // Generate AI suggestions
    const handleGenerateSuggestions = async () => {
        setGenerating(true);
        setError(null);
        try {
            // Get semantic mappings for the source, then generate question suggestions
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}`);
            if (response.ok) {
                const data = await response.json();
                const mappings = data.semantic_mappings || {};
                
                // Helper: Check if sample values look numeric
                const hasNumericSamples = (samples: any[]): boolean => {
                    if (!samples || samples.length === 0) return true; // Assume numeric if no samples
                    // Check if most samples parse as numbers
                    const numericCount = samples.filter(s => {
                        if (typeof s === 'number') return true;
                        if (typeof s === 'string') {
                            const cleaned = s.replace(/[$€£¥₪,]/g, '').trim();
                            return !isNaN(parseFloat(cleaned)) && isFinite(Number(cleaned));
                        }
                        return false;
                    }).length;
                    return numericCount >= samples.length / 2; // At least half must be numeric
                };
                
                // Generate suggested questions based on mappings
                const suggestions: ExampleQuestion[] = [];
                
                for (const [tableName, columns] of Object.entries(mappings)) {
                    const colArray = columns as any[];
                    
                    // STRICT measure detection - ONLY truly numeric/aggregatable columns
                    // Measures must have explicit numeric semantic_category AND numeric sample values
                    const numericCategories = ['financial', 'quantity', 'percentage', 'currency', 'numeric'];
                    const measureColumns = colArray.filter((c: any) => {
                        const category = (c.semantic_category || c.category || '').toLowerCase();
                        const role = (c.business_role || '').toLowerCase();
                        
                        // Check if marked as numeric category or metric role
                        const hasNumericMarking = numericCategories.includes(category) || role === 'metric';
                        if (!hasNumericMarking) return false;
                        
                        // ADDITIONAL CHECK: Verify sample values look numeric
                        const samples = c.sample_values || [];
                        if (samples.length > 0 && !hasNumericSamples(samples)) {
                            console.log(`[ExampleQuestions] Excluding ${c.column_name} - sample values not numeric:`, samples);
                            return false;
                        }
                        
                        return true;
                    });
                    
                    // STRICT dimension detection - categorical/grouping columns
                    const dimensionCategories = ['category', 'location', 'product', 'customer', 'status', 'text', 'identifier'];
                    const dimensionRoles = ['dimension', 'filter', 'attribute', 'label', 'primary_key', 'foreign_key'];
                    const dimensionColumns = colArray.filter((c: any) => {
                        const category = (c.semantic_category || c.category || '').toLowerCase();
                        const role = (c.business_role || '').toLowerCase();
                        // Exclude anything that's a measure
                        const isMeasure = measureColumns.some(m => m.column_name === c.column_name);
                        if (isMeasure) return false;
                        // Check for dimension indicators
                        return dimensionCategories.includes(category) || dimensionRoles.includes(role);
                    });
                    
                    // Date columns
                    const dateCategories = ['date', 'datetime'];
                    const dateColumns = colArray.filter((c: any) => {
                        const category = (c.semantic_category || c.category || '').toLowerCase();
                        const role = (c.business_role || '').toLowerCase();
                        return dateCategories.includes(category) || role === 'timestamp' || c.is_time_column === true;
                    });
                    
                    // For text-heavy tables without numeric measures, generate listing/search questions
                    if (measureColumns.length === 0) {
                        console.log(`[ExampleQuestions] Table ${tableName} has no numeric measures - generating text-based questions`);
                        console.log(`[ExampleQuestions] Columns:`, colArray.map((c: any) => ({ 
                            name: c.column_name, 
                            category: c.semantic_category || c.category, 
                            role: c.business_role 
                        })));
                        
                        // Get text/identifier columns for search/filter questions
                        const textColumns = colArray.filter((c: any) => {
                            const category = (c.semantic_category || c.category || '').toLowerCase();
                            const role = (c.business_role || '').toLowerCase();
                            return ['text', 'identifier', 'name', 'description', 'category', 'product', 'customer', 'label'].includes(category) ||
                                   ['attribute', 'label', 'filter', 'dimension'].includes(role) ||
                                   c.column_name?.toLowerCase().includes('name') ||
                                   c.column_name?.toLowerCase().includes('description');
                        });
                        
                        // Basic listing question
                        suggestions.push({
                            question: `Show all ${tableName.replace(/^v/, '').replace(/([A-Z])/g, ' $1').trim().toLowerCase()}`,
                            query_pattern: `SELECT * FROM ${tableName}`,
                            explanation: `List all records from ${tableName}`,
                            tables_used: [tableName],
                            columns_used: colArray.map((c: any) => c.column_name),
                            calculation_hint: `SELECT all columns`,
                            sql_template: `SELECT * FROM ${tableName}`,
                            target_columns: colArray.slice(0, 5).map((c: any) => c.column_name),
                            filter_hints: [],
                            expected_aggregation: 'none'
                        });
                        
                        // Search/filter questions for text columns
                        for (const textCol of textColumns.slice(0, 2)) {
                            const colName = textCol.business_name || textCol.column_name;
                            const cleanName = colName.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
                            
                            suggestions.push({
                                question: `Find records where ${cleanName} contains a specific term`,
                                query_pattern: `SELECT * FROM ${tableName} WHERE ${textCol.column_name} LIKE '%search_term%'`,
                                explanation: `Filter ${tableName} by searching in ${colName}`,
                                tables_used: [tableName],
                                columns_used: [textCol.column_name],
                                calculation_hint: `WHERE ${textCol.column_name} LIKE '%term%'`,
                                sql_template: `SELECT * FROM ${tableName} WHERE ${textCol.column_name} LIKE '%search_term%'`,
                                target_columns: [textCol.column_name],
                                filter_hints: [`Search by ${cleanName}`],
                                expected_aggregation: 'none'
                            });
                        }
                        
                        // Count question
                        suggestions.push({
                            question: `How many records are in ${tableName.replace(/^v/, '').replace(/([A-Z])/g, ' $1').trim().toLowerCase()}?`,
                            query_pattern: `SELECT COUNT(*) FROM ${tableName}`,
                            explanation: `Count total records in ${tableName}`,
                            tables_used: [tableName],
                            columns_used: [],
                            calculation_hint: `COUNT(*)`,
                            sql_template: `SELECT COUNT(*) AS record_count FROM ${tableName}`,
                            target_columns: [],
                            filter_hints: [],
                            expected_aggregation: 'count'
                        });
                        
                        // Distinct values question for dimension columns
                        if (dimensionColumns.length > 0) {
                            const dim = dimensionColumns[0];
                            const dimName = dim.business_name || dim.column_name;
                            suggestions.push({
                                question: `What are the distinct ${dimName.replace(/([A-Z])/g, ' $1').trim().toLowerCase()} values?`,
                                query_pattern: `SELECT DISTINCT ${dim.column_name} FROM ${tableName}`,
                                explanation: `List unique ${dimName} values`,
                                tables_used: [tableName],
                                columns_used: [dim.column_name],
                                calculation_hint: `DISTINCT ${dim.column_name}`,
                                sql_template: `SELECT DISTINCT ${dim.column_name} FROM ${tableName}`,
                                target_columns: [dim.column_name],
                                filter_hints: [],
                                expected_aggregation: 'none'
                            });
                        }
                        
                        continue;
                    }
                    
                    console.log(`[ExampleQuestions] Table ${tableName}:`, {
                        measures: measureColumns.map((c: any) => c.column_name || c.business_name),
                        dimensions: dimensionColumns.map((c: any) => c.column_name || c.business_name),
                        dates: dateColumns.map((c: any) => c.column_name || c.business_name)
                    });
                    
                    // 1. Total aggregation questions for each measure
                    for (const measure of measureColumns.slice(0, 3)) {
                        const measureName = measure.business_name || measure.column_name;
                        // Handle "none" as no-op - default to SUM for totals
                        const rawAgg = measure.default_aggregation || measure.aggregation_type || 'SUM';
                        const agg = (rawAgg.toLowerCase() === 'none') ? 'SUM' : rawAgg;
                        const sqlQuery = `SELECT ${agg}(${measure.column_name}) AS total_${measure.column_name} FROM ${tableName}`;
                        suggestions.push({
                            question: `What is the total ${measureName}?`,
                            query_pattern: sqlQuery,
                            explanation: `Calculate the sum of all ${measureName} values`,
                            tables_used: [tableName],
                            columns_used: [measure.column_name],
                            // AI Query Guidance fields
                            calculation_hint: `${agg}(${measure.column_name})`,
                            sql_template: sqlQuery,
                            target_columns: [measure.column_name],
                            filter_hints: [],
                            expected_aggregation: agg.toLowerCase()
                        });
                    }
                    
                    // 2. Breakdown by dimension questions
                    for (const dimension of dimensionColumns.slice(0, 2)) {
                        for (const measure of measureColumns.slice(0, 2)) {
                            const measureName = measure.business_name || measure.column_name;
                            const dimensionName = dimension.business_name || dimension.column_name;
                            const rawAgg2 = measure.default_aggregation || measure.aggregation_type || 'SUM';
                            const agg = (rawAgg2.toLowerCase() === 'none') ? 'SUM' : rawAgg2;
                            const sqlQuery = `SELECT ${dimension.column_name}, ${agg}(${measure.column_name}) AS total FROM ${tableName} GROUP BY ${dimension.column_name} ORDER BY total DESC`;
                            
                            suggestions.push({
                                question: `What is the ${measureName} by ${dimensionName}?`,
                                query_pattern: sqlQuery,
                                explanation: `Show ${measureName} grouped by each ${dimensionName}`,
                                tables_used: [tableName],
                                columns_used: [measure.column_name, dimension.column_name],
                                // AI Query Guidance fields
                                calculation_hint: `${agg}(${measure.column_name}) grouped by ${dimension.column_name}`,
                                sql_template: sqlQuery,
                                target_columns: [measure.column_name, dimension.column_name],
                                filter_hints: [`Filter by ${dimensionName}`],
                                expected_aggregation: agg.toLowerCase()
                            });
                        }
                    }
                    
                    // 3. Time-based analysis
                    if (dateColumns.length > 0 && measureColumns.length > 0) {
                        const dateCol = dateColumns[0];
                        const measure = measureColumns[0];
                        const measureName = measure.business_name || measure.column_name;
                        const dateName = dateCol.business_name || dateCol.column_name;
                        const rawAgg3 = measure.default_aggregation || measure.aggregation_type || 'SUM';
                        const agg = (rawAgg3.toLowerCase() === 'none') ? 'SUM' : rawAgg3;
                        const sqlQuery = `SELECT ${dateCol.column_name}, ${agg}(${measure.column_name}) AS total FROM ${tableName} GROUP BY ${dateCol.column_name} ORDER BY ${dateCol.column_name}`;
                        
                        suggestions.push({
                            question: `How has ${measureName} changed over time?`,
                            query_pattern: sqlQuery,
                            explanation: `Time series trend of ${measureName} by ${dateName}`,
                            tables_used: [tableName],
                            columns_used: [measure.column_name, dateCol.column_name],
                            // AI Query Guidance fields
                            calculation_hint: `${agg}(${measure.column_name}) over time by ${dateCol.column_name}`,
                            sql_template: sqlQuery,
                            target_columns: [measure.column_name, dateCol.column_name],
                            filter_hints: [`Filter by date range`, `Filter by ${dateName}`],
                            expected_aggregation: agg.toLowerCase()
                        });
                    }
                    
                    // 4. Top N analysis
                    if (dimensionColumns.length > 0 && measureColumns.length > 0) {
                        const dimension = dimensionColumns[0];
                        const measure = measureColumns[0];
                        const measureName = measure.business_name || measure.column_name;
                        const dimensionName = dimension.business_name || dimension.column_name;
                        const rawAgg4 = measure.default_aggregation || measure.aggregation_type || 'SUM';
                        const agg = (rawAgg4.toLowerCase() === 'none') ? 'SUM' : rawAgg4;
                        const sqlQuery = `SELECT TOP 10 ${dimension.column_name}, ${agg}(${measure.column_name}) AS total FROM ${tableName} GROUP BY ${dimension.column_name} ORDER BY total DESC`;
                        
                        suggestions.push({
                            question: `What are the top 10 ${dimensionName}s by ${measureName}?`,
                            query_pattern: sqlQuery,
                            explanation: `Ranking of ${dimensionName} by highest ${measureName}`,
                            tables_used: [tableName],
                            columns_used: [measure.column_name, dimension.column_name],
                            // AI Query Guidance fields
                            calculation_hint: `TOP 10 by ${agg}(${measure.column_name}) grouped by ${dimension.column_name}`,
                            sql_template: sqlQuery,
                            target_columns: [measure.column_name, dimension.column_name],
                            filter_hints: [],
                            expected_aggregation: agg.toLowerCase()
                        });
                    }
                    
                    // 5. Average questions
                    if (measureColumns.length > 0) {
                        const measure = measureColumns[0];
                        const measureName = measure.business_name || measure.column_name;
                        const sqlQuery = `SELECT AVG(${measure.column_name}) AS average_${measure.column_name} FROM ${tableName}`;
                        
                        suggestions.push({
                            question: `What is the average ${measureName}?`,
                            query_pattern: sqlQuery,
                            explanation: `Calculate the mean value of ${measureName}`,
                            tables_used: [tableName],
                            columns_used: [measure.column_name],
                            // AI Query Guidance fields
                            calculation_hint: `AVG(${measure.column_name})`,
                            sql_template: sqlQuery,
                            target_columns: [measure.column_name],
                            filter_hints: [],
                            expected_aggregation: 'average'
                        });
                    }
                }
                
                if (suggestions.length > 0) {
                    const uniqueSuggestions = suggestions.filter((s, idx, arr) => 
                        arr.findIndex(q => q.question === s.question) === idx
                    );
                    setQuestions([...questions, ...uniqueSuggestions]);
                    setSuccess(`Added ${uniqueSuggestions.length} suggested questions`);
                } else {
                    setError('No suggestions generated. Make sure columns are properly marked: numeric columns should have semantic_category set to "financial", "quantity", or "percentage", and business_role set to "metric" for aggregatable values.');
                }
            }
        } catch (err) {
            setError('Error generating suggestions');
        } finally {
            setGenerating(false);
        }
    };
    
    // Save questions
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            // Filter out empty questions
            const validQuestions = questions.filter(q => q.question.trim());
            
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}/example-questions`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ questions: validQuestions })
            });
            
            if (response.ok) {
                setSuccess('Example questions saved successfully');
                // Don't call onSave - it causes parent to refetch and close/reset the panel
                // onSave?.();
            } else {
                setError('Failed to save example questions');
            }
        } catch (err) {
            setError('Error saving example questions');
        } finally {
            setSaving(false);
        }
    };
    
    // Render question card
    const renderQuestionCard = (question: ExampleQuestion, index: number) => {
        const isExpanded = expandedQuestions.has(index);
        
        return (
        <div key={index} className={styles.questionCard}>
            <div className={styles.questionHeader}>
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                    <IconButton
                        iconProps={{ iconName: isExpanded ? 'ChevronDown' : 'ChevronRight' }}
                        title={isExpanded ? 'Collapse' : 'Expand'}
                        onClick={() => toggleExpand(index)}
                        styles={{ 
                            root: { 
                                width: '28px', 
                                height: '28px',
                                color: '#666'
                            },
                            icon: { fontSize: '12px' }
                        }}
                    />
                    <span className={styles.questionNumber}>{index + 1}</span>
                    <Stack grow>
                        <TextField
                            placeholder="Enter example question users might ask..."
                            value={question.question}
                            onChange={(_, val) => updateQuestion(index, 'question', val || '')}
                            styles={{ root: { width: '100%' }, field: { fontSize: '14px', fontWeight: FontWeights.semibold } }}
                        />
                    </Stack>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 4 }}>
                    {!isExpanded && question.target_columns && question.target_columns.length > 0 && (
                        <Text variant="small" styles={{ root: { color: '#666', marginRight: '8px', alignSelf: 'center' } }}>
                            {question.target_columns.length} col{question.target_columns.length !== 1 ? 's' : ''}
                            {question.expected_aggregation ? ` • ${question.expected_aggregation}` : ''}
                        </Text>
                    )}
                    <TooltipHost content="Auto-detect tables and columns from question text">
                        <IconButton
                            onRenderIcon={() => <WandRegular />}
                            title="Auto-detect tables/columns"
                            onClick={() => handleAutoDetect(index)}
                            disabled={detectingIndex === index || !question.question?.trim()}
                            styles={{ 
                                root: { color: '#667eea' },
                                rootDisabled: { color: '#ccc' }
                            }}
                        />
                    </TooltipHost>
                    <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title="Remove question"
                        onClick={() => handleRemoveQuestion(index)}
                        styles={{ root: { color: '#d13438' } }}
                    />
                </Stack>
            </div>
            
            {isExpanded && (
            <Stack tokens={stackTokens}>
                <TextField
                    label="Explanation"
                    placeholder="Explain what this question is asking for..."
                    value={question.explanation || ''}
                    onChange={(_, val) => updateQuestion(index, 'explanation', val || '')}
                    multiline
                    rows={2}
                />
                
                <TextField
                    label="Query Pattern (Optional)"
                    placeholder="SELECT ... FROM ... WHERE ..."
                    value={question.query_pattern || ''}
                    onChange={(_, val) => updateQuestion(index, 'query_pattern', val || '')}
                    styles={{ field: { fontFamily: 'monospace', fontSize: '12px' } }}
                />
                <Text className={styles.helpText}>
                    A SQL-like pattern to help the AI understand how to answer this question
                </Text>
                
                {/* Tables and Columns with available suggestions */}
                <div className={styles.fieldRow}>
                    <Stack>
                        <TagInput
                            label="Tables Used"
                            value={question.tables_used || []}
                            onChange={(tags) => updateQuestion(index, 'tables_used', tags)}
                            placeholder="Add table name..."
                            suggestions={tablesInfo.map(t => t.name)}
                        />
                        {tablesInfo.length > 0 && (
                            <Text className={styles.helpText}>
                                Available: {tablesInfo.map(t => t.name).slice(0, 3).join(', ')}{tablesInfo.length > 3 ? '...' : ''}
                            </Text>
                        )}
                    </Stack>
                    <Stack>
                        <TagInput
                            label="Columns Used"
                            value={question.columns_used || []}
                            onChange={(tags) => updateQuestion(index, 'columns_used', tags)}
                            placeholder="Add column name..."
                            suggestions={tablesInfo.flatMap(t => t.columns.map(c => c.column_name))}
                        />
                    </Stack>
                </div>
                
                {/* NEW: Critical fields for AI */}
                <Stack tokens={{ childrenGap: 12 }} styles={{ root: { 
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#f0f7ff',
                    borderRadius: '4px',
                    border: '1px solid #cce4ff'
                } }}>
                    <Label style={{ color: '#0066cc', fontWeight: 600 }}>🧠 AI Query Guidance</Label>
                    
                    <TextField
                        label="Calculation Hint"
                        value={question.calculation_hint || ''}
                        onChange={(_, val) => updateQuestion(index, 'calculation_hint', val || '')}
                        placeholder="e.g., SUM(Sales) grouped by Region; calculate profit margin as Profit/Revenue*100"
                        description="Brief formula or approach (for developers)"
                    />
                    
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <Label style={{ color: '#d97706', fontWeight: 600 }}>📝 Worked Example (Step-by-Step)</Label>
                        <TooltipHost
                            content={
                                <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: '12px', maxWidth: '400px' } }}>
                                    <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>💡 How to Write Good Worked Examples</Text>
                                    <Text variant="small">
                                        <strong>Purpose:</strong> Teach the AI exactly how to calculate the answer step-by-step.
                                    </Text>
                                    <Text variant="small"><strong>1. Start with filtering:</strong> "Filter to rows where Vessel_Type = 'Container'"</Text>
                                    <Text variant="small"><strong>2. Show the math:</strong> "Sum = 435.1M from 15 vessels"</Text>
                                    <Text variant="small"><strong>3. Show the formula:</strong> "Average = Sum / Count = 435.1M / 14 = 31.08M"</Text>
                                    <Text variant="small"><strong>4. Include real numbers:</strong> Use actual values from your data</Text>
                                    <Separator />
                                    <Text variant="small" styles={{ root: { color: '#666' } }}>
                                        <strong>Example:</strong><br/>
                                        "1. Filter: Select rows where Type = 'Container'<br/>
                                        2. Result: 15 vessels found<br/>
                                        3. Calculate: Sum(Price) = $435.1M<br/>
                                        4. Count valid: 14 vessels have prices<br/>
                                        5. Average: 435.1M / 14 = $31.08M"
                                    </Text>
                                    <Text variant="small" styles={{ root: { color: '#0066cc', fontStyle: 'italic' } }}>
                                        ✅ Supports Hebrew + English matching!
                                    </Text>
                                </Stack>
                            }
                            calloutProps={{ gapSpace: 0 }}
                        >
                            <IconButton
                                iconProps={{ iconName: 'Info' }}
                                title="Help writing worked examples"
                                styles={{ 
                                    root: { color: '#0066cc' },
                                    rootHovered: { color: '#004499' }
                                }}
                            />
                        </TooltipHost>
                    </Stack>
                    <TextField
                        multiline
                        rows={4}
                        value={question.worked_example || ''}
                        onChange={(_, val) => updateQuestion(index, 'worked_example', val || '')}
                        placeholder={`For a question like "What is the average price for container vessels?":
1. Filter: Select only rows where Vessel_Type = 'Container'
2. Filter: Apply date range (last 3 months)
3. Calculate: Average = Sum of Price_m_ / Count of valid prices
4. Example result: 15 vessels found, Avg = 435.1M / 14 = $31.08M`}
                        description="Write in plain English how to calculate the answer. Include real numbers and step-by-step logic. This is the MOST IMPORTANT field for accurate AI calculations!"
                        styles={{ 
                            root: { marginTop: '4px' },
                            field: { backgroundColor: '#fffef5', minHeight: '100px' },
                            description: { color: '#d97706', fontWeight: 500 }
                        }}
                    />
                    
                    {/* Test Calculation Button */}
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { marginTop: '8px' } }}>
                        <DefaultButton
                            text={testingIndex === index ? "Testing..." : "🧪 Test Calculation"}
                            onClick={() => handleTestCalculation(index)}
                            disabled={testingIndex !== null || !question.question}
                            styles={{ 
                                root: { 
                                    backgroundColor: '#e8f5e9',
                                    borderColor: '#4caf50'
                                }
                            }}
                        />
                        {testResult?.index === index && (
                            <Stack styles={{ root: { 
                                padding: '8px 12px', 
                                backgroundColor: testResult.result.success ? '#e8f5e9' : '#ffebee',
                                borderRadius: '4px',
                                border: `1px solid ${testResult.result.success ? '#4caf50' : '#f44336'}`,
                                flex: 1
                            } }}>
                                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                                    {testResult.result.success ? '✅ Result:' : '❌ Error:'}
                                </Text>
                                {testResult.result.success ? (
                                    <>
                                        <Text variant="small">
                                            {testResult.result.aggregation}({testResult.result.metric_column}) = <strong>{testResult.result.result}</strong>
                                        </Text>
                                        <Text variant="xSmall" styles={{ root: { color: '#666' } }}>
                                            {testResult.result.rows_before_filter} → {testResult.result.rows_after_filter} rows | {testResult.result.valid_values} valid values
                                        </Text>
                                        {testResult.result.calculation_steps && (
                                            <details style={{ marginTop: '4px', fontSize: '11px' }}>
                                                <summary style={{ cursor: 'pointer' }}>View calculation steps</summary>
                                                <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                                                    {testResult.result.calculation_steps.map((step: string, i: number) => (
                                                        <li key={i}>{step}</li>
                                                    ))}
                                                </ul>
                                            </details>
                                        )}
                                    </>
                                ) : (
                                    <Text variant="small">{testResult.result.error}</Text>
                                )}
                            </Stack>
                        )}
                    </Stack>
                    
                    <TextField
                        label="SQL Template"
                        value={question.sql_template || ''}
                        onChange={(_, val) => updateQuestion(index, 'sql_template', val || '')}
                        placeholder="SELECT Region, SUM(Sales) as Total_Sales FROM data GROUP BY Region ORDER BY Total_Sales DESC"
                        styles={{ field: { fontFamily: 'monospace', fontSize: '12px' } }}
                        description="Example SQL the AI can reference when generating pandas code"
                    />
                    
                    <div className={styles.fieldRow}>
                        <TagInput
                            label="Target Columns (specific columns to use)"
                            value={question.target_columns || []}
                            onChange={(tags) => updateQuestion(index, 'target_columns', tags)}
                            placeholder="Add column..."
                            suggestions={tablesInfo.flatMap(t => t.columns.map(c => c.column_name))}
                        />
                        <TagInput
                            label="Filter Hints"
                            value={question.filter_hints || []}
                            onChange={(tags) => updateQuestion(index, 'filter_hints', tags)}
                            placeholder="e.g., Year=2024, Region='North America'"
                        />
                    </div>
                    
                    <Dropdown
                        label="Expected Aggregation"
                        selectedKey={question.expected_aggregation || ''}
                        options={[
                            { key: '', text: 'None / Auto-detect' },
                            { key: 'sum', text: 'Sum' },
                            { key: 'average', text: 'Average' },
                            { key: 'count', text: 'Count' },
                            { key: 'min', text: 'Minimum' },
                            { key: 'max', text: 'Maximum' },
                            { key: 'count_distinct', text: 'Count Distinct' },
                            { key: 'ratio', text: 'Ratio / Percentage' },
                            { key: 'median', text: 'Median' }
                        ]}
                        onChange={(_, option) => updateQuestion(index, 'expected_aggregation', option?.key as string)}
                        styles={{ root: { maxWidth: '200px' } }}
                    />
                </Stack>
            </Stack>
            )}
        </div>
    );
    };
    
    if (loading) {
        return (
            <Stack horizontalAlign="center" styles={{ root: { padding: '40px' } }}>
                <Spinner size={SpinnerSize.large} label="Loading example questions..." />
            </Stack>
        );
    }
    
    return (
        <div className={styles.container}>
            {/* Messages */}
            {error && (
                <MessageBar 
                    messageBarType={MessageBarType.error}
                    onDismiss={() => setError(null)}
                    styles={{ root: { marginBottom: '16px' } }}
                >
                    {error}
                </MessageBar>
            )}
            
            {success && (
                <MessageBar 
                    messageBarType={MessageBarType.success}
                    onDismiss={() => setSuccess(null)}
                    styles={{ root: { marginBottom: '16px' } }}
                >
                    {success}
                </MessageBar>
            )}
            
            {/* Info Panel */}
            <div className={styles.suggestionsPanel}>
                <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                    <InfoRegular style={{ fontSize: '20px', color: '#0078d4' }} />
                    <Stack grow>
                        <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>
                            Example Questions Help AI Understand Complex Queries
                        </Text>
                        <Text variant="small" style={{ color: '#666' }}>
                            Add real-world questions users might ask. Include query patterns and explanations to help the AI
                            understand business rules and complex calculations that can't be inferred from column names alone.
                        </Text>
                    </Stack>
                </Stack>
            </div>
            
            {/* Actions */}
            <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginBottom: '20px' } }} wrap>
                <PrimaryButton
                    iconProps={{ iconName: 'Add' }}
                    text="Add Question"
                    onClick={handleAddQuestion}
                />
                <DefaultButton
                    iconProps={{ iconName: 'Lightbulb' }}
                    text="Generate Suggestions"
                    onClick={handleGenerateSuggestions}
                    disabled={generating}
                />
                <DefaultButton
                    onRenderIcon={() => <WandRegular />}
                    text="Auto-detect All"
                    onClick={() => {
                        let detected = 0;
                        questions.forEach((_, idx) => {
                            if (questions[idx]?.question?.trim()) {
                                handleAutoDetect(idx);
                                detected++;
                            }
                        });
                        if (detected > 0) {
                            setSuccess(`Auto-detected tables/columns for ${detected} questions`);
                            setTimeout(() => setSuccess(null), 3000);
                        }
                    }}
                    disabled={questions.length === 0 || tablesInfo.length === 0}
                    title="Auto-detect tables and columns for all questions"
                />
                <DefaultButton
                    iconProps={{ iconName: 'Upload' }}
                    text="Bulk Import JSON"
                    onClick={() => {
                        setBulkJsonText('');
                        setBulkImportError(null);
                        setShowBulkImport(true);
                    }}
                    title="Import multiple questions from JSON"
                />
                <Separator vertical styles={{ root: { height: '24px', alignSelf: 'center' } }} />
                <DefaultButton
                    iconProps={{ iconName: 'ExploreContent' }}
                    text="Expand All"
                    onClick={expandAll}
                    disabled={questions.length === 0}
                    title="Expand all questions"
                />
                <DefaultButton
                    iconProps={{ iconName: 'CollapseContent' }}
                    text="Collapse All"
                    onClick={collapseAll}
                    disabled={questions.length === 0}
                    title="Collapse all questions"
                />
                {generating && <Spinner size={SpinnerSize.small} label="Generating..." />}
                
                <Stack grow horizontalAlign="end">
                    <PrimaryButton
                        iconProps={{ iconName: 'Save' }}
                        text="Save Questions"
                        onClick={handleSave}
                        disabled={saving}
                    />
                </Stack>
            </Stack>
            
            <Separator />
            
            {/* Questions List */}
            {questions.length === 0 ? (
                <Stack horizontalAlign="center" styles={{ root: { padding: '40px', backgroundColor: '#f5f5f5', borderRadius: '8px' } }}>
                    <QuestionCircleRegular style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
                    <Text variant="large" style={{ color: '#666' }}>No example questions yet</Text>
                    <Text variant="small" style={{ color: '#999', marginTop: '8px' }}>
                        Add questions to help the AI understand how users will query this data
                    </Text>
                    <DefaultButton
                        iconProps={{ iconName: 'Add' }}
                        text="Add First Question"
                        onClick={handleAddQuestion}
                        styles={{ root: { marginTop: '16px' } }}
                    />
                </Stack>
            ) : (
                <Stack styles={{ root: { marginTop: '16px' } }}>
                    {questions.map(renderQuestionCard)}
                </Stack>
            )}
            
            {/* Bulk Import Dialog */}
            <Dialog
                hidden={!showBulkImport}
                onDismiss={() => setShowBulkImport(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Bulk Import Example Questions',
                    subText: 'Paste a JSON array of example questions. Each question should have at least a "question" field.'
                }}
                modalProps={{
                    isBlocking: false,
                    styles: { main: { minWidth: '700px', maxWidth: '900px' } }
                }}
            >
                <Stack tokens={{ childrenGap: 16 }}>
                    {bulkImportError && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px' }}>
                                {bulkImportError}
                            </pre>
                        </MessageBar>
                    )}
                    
                    <TextField
                        label="JSON Array"
                        multiline
                        rows={15}
                        value={bulkJsonText}
                        onChange={(_, val) => setBulkJsonText(val || '')}
                        placeholder={`[\n  {\n    "question": "What is the average charter rate for container vessels?",\n    "target_tables": ["Vessel_Chartering.xlsx:Sheet2"],\n    "target_columns": ["Hire_pd", "vessel_type"],\n    "calculation_hint": "AVG(Hire_pd) WHERE vessel_type='container'",\n    "filter_hints": ["container vessels", "last 3 months"],\n    "expected_aggregation": "average",\n    "worked_example": "14 fixtures found. Sum = 434,800. Avg = 31,057/day"\n  }\n]`}
                        styles={{
                            field: { fontFamily: 'Consolas, Monaco, monospace', fontSize: '13px' }
                        }}
                    />
                    
                    <MessageBar messageBarType={MessageBarType.info}>
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>Supported fields:</Text>
                            <Text variant="small">
                                <b>question</b> (required), <b>target_tables</b> (array), <b>target_columns</b> (array), 
                                <b>calculation_hint</b>, <b>filter_hints</b> (array), <b>expected_aggregation</b> (sum/average/count/etc), 
                                <b>worked_example</b>, <b>sql_template</b>
                            </Text>
                        </Stack>
                    </MessageBar>
                </Stack>
                
                <DialogFooter>
                    <PrimaryButton 
                        text={`Import Questions`}
                        onClick={handleBulkImport}
                        iconProps={{ iconName: 'Upload' }}
                    />
                    <DefaultButton 
                        text="Cancel" 
                        onClick={() => setShowBulkImport(false)} 
                    />
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default ExampleQuestionsEditor;
